import type {
  InvoiceV2PartyRole,
  InvoiceV2PartySuggestionsPayload,
  InvoiceV2PartySuggestionsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  zodInvoiceV2PartySuggestionsQueryParamsSchema,
  zodInvoiceV2PartySuggestionsResponseSchema,
} from './validations';

const SUGGESTIONS_ENDPOINT: Record<InvoiceV2PartyRole, () => string> = {
  buyer: ENDPOINT.INVOICE.GET_BUYER_SUGGESTIONS,
  issuer: ENDPOINT.INVOICE.GET_ISSUER_SUGGESTIONS,
};

/**
 * Fetches the companies that appear as issuer or buyer on the invoices the
 * caller can read, for the invoice list party filters.
 *
 * @param payload - Which role to suggest, and an optional name substring and result limit (1–50).
 * @param options - Additional request options (e.g. abort signal).
 * @returns The ranked party suggestions.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_issuers_invoices_suggestions_issuers_get
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_buyers_invoices_suggestions_buyers_get
 */
export const fetchInvoiceV2PartySuggestions = async (
  { queryParams = {}, role }: InvoiceV2PartySuggestionsPayload,
  options?: KyRequestOptions,
) => {
  return await vestigasApi
    .get(SUGGESTIONS_ENDPOINT[role](), {
      ...options,
      searchParams: queryParams,
      validate: {
        ...options?.validate,
        response: zodInvoiceV2PartySuggestionsResponseSchema,
        searchParams: zodInvoiceV2PartySuggestionsQueryParamsSchema.transform(
          (data) => withoutObjectKeysWhereValueIs(data, [undefined, '']),
        ),
      },
    })
    .json<InvoiceV2PartySuggestionsResponse>();
};
